import { useParams, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Calendar, Download } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card, CardStat } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { ProjectStatusBadge } from "@/components/ui/Badges";
import { useProject } from "@/hooks/useProjects";
import { api } from "@/lib/api";
import AiBrainPanel from "@/components/AiBrainPanel";
import { useOrgContext } from "@/hooks/useOrgContext";

export default function ProjectDetail() {
  const params = useParams<{ id: string }>();
  const { data: orgContextData } = useOrgContext();
  const { data: project, isLoading, error } = useProject(params.id);

  if (isLoading) {
    return (
      <Layout>
        <PageHeader title="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <PageHeader title="Project not found" />
        <div className="p-8">
          <Card>
            <p className="text-sm text-red-400 font-medium">
              {(error as Error)?.message ?? "Project not found"}
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  const matrix = project.matrix ?? [];
  const documents = project.documents ?? [];
  const sections = project.sections ?? [];
  const qaFindings = project.qaFindings ?? [];
  const matrixComplete = matrix.filter((i) => i.status === "complete").length;
  const docsRequired = documents.filter((d) => d.required);
  const docsComplete = docsRequired.filter((d) => d.status === "complete");
  const progress = matrix.length > 0 ? Math.round((matrixComplete / matrix.length) * 100) : 0;

  const deadline = project.submissionDeadline ? new Date(project.submissionDeadline) : null;
  const daysLeft = deadline
    ? Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Layout projectTitle={project.title} projectId={project.id}>
      <PageHeader
        title={project.title}
        subtitle={project.requesterName}
        actions={
          <>
            <ExportButton projectId={project.id} />
            <Link href={`/projects/${project.id}/matrix`}>
              <Button variant="primary" size="sm">
                Open Response Matrix
              </Button>
            </Link>
          </>
        }
      />

      <div className="p-8 space-y-6">
        <AiBrainPanel
          project={project}
          projectId={project.id}
          orgContext={orgContextData?.context}
        />

        <Card>
          <div className="flex items-center gap-3">
            <ProjectStatusBadge status={project.status} />
            {deadline && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar
                  size={14}
                  style={{ color: daysLeft && daysLeft < 14 ? "#ef4444" : "#6B7280" }}
                />
                <span style={{ color: "#6B7280" }}>Deadline:</span>
                <span
                  className="font-medium"
                  style={{
                    color: daysLeft && daysLeft < 14 ? "#ef4444" : "#FFFFFF",
                  }}
                >
                  {deadline.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                  {daysLeft !== null && ` — ${daysLeft} days remaining`}
                </span>
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400">Response Completion</span>
              <span className="text-xs font-bold text-white">{progress}%</span>
            </div>
            <div className="h-2 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.05)" }}>
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  width: `${progress}%`,
                  backgroundColor: progress === 100 ? "#22c55e" : "#F97316",
                }}
              />
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <CardStat label="Matrix Items" value={matrix.length} sub={`${matrixComplete} complete`} />
          <CardStat label="Sections" value={sections.length} sub="drafts" />
          <CardStat
            label="Documents"
            value={`${docsComplete.length}/${docsRequired.length}`}
            sub="collected"
          />
          <CardStat label="QA Findings" value={qaFindings.length} />
        </div>

        {!project.summary && (
          <Card>
            <p className="text-sm font-medium text-white">
              {project.rfpFileId ? "Reading the RFP…" : "Add the RFP file or text to get started"}
            </p>
            <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
              {project.rfpFileId ? (
                <>
                  The AI is extracting key details. Open the{" "}
                  <Link href={`/projects/${project.id}/summary`}>
                    <span className="text-orange-400 font-medium hover:underline cursor-pointer">
                      Summary
                    </span>
                  </Link>{" "}
                  page to watch progress.
                </>
              ) : (
                <>
                  Open the{" "}
                  <Link href={`/projects/${project.id}/summary`}>
                    <span className="text-orange-400 font-medium hover:underline cursor-pointer">
                      Summary
                    </span>
                  </Link>{" "}
                  page to upload the source.
                </>
              )}
            </p>
          </Card>
        )}
      </div>
    </Layout>
  );
}

/**
 * Export button — POSTs `/projects/{id}/export`, then triggers the browser
 * to download the returned presigned URL. We use an anchor element so the
 * browser handles the file save dialog without us needing to stream bytes
 * through the React app.
 */
function ExportButton({ projectId }: { projectId: string }) {
  const exporter = useMutation({
    mutationFn: () => api.exportProject(projectId, "docx"),
    onSuccess({ downloadUrl }) {
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.rel = "noopener";
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Proposal DOCX ready");
    },
    onError(err) {
      toast.error((err as Error).message);
    },
  });

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => exporter.mutate()}
      disabled={exporter.isPending}
      icon={
        exporter.isPending ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />
      }
    >
      {exporter.isPending ? "Building…" : "Export DOCX"}
    </Button>
  );
}
